/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { BaseLegendItem } from './BaseLegendItem';
import { getItemProps, getGridCellIndices } from '#utils/PRIVATE_legendUtils/legendUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { styles } from './themes/LegendStyles.css';
import { BaseLegendProps, LegendItemData } from '#utils/UNSAFE_visTypes/legend';

/**
 * The controlled legend reponsible only for rendering the legend items.
 */
export const BaseLegend = <
  K extends string | number,
  D extends LegendItemData<K> = LegendItemData<K>
>({
  orientation = 'horizontal',
  highlightedIds,
  hiddenIds,
  focusedItemInfo,
  hoveredItemInfo,
  sectionIdx,
  activeId,
  isReadOnly,
  items,
  symbolHeight,
  symbolWidth,
  hideAndShowBehavior = 'off',
  hoverBehavior = 'none',
  isTestEnv,
  ...props
}: BaseLegendProps<K, D>) => {
  const isHoriz = orientation === 'horizontal';
  const columTemplates = isHoriz ? `repeat(${props.itemsPerRow}, ${props.itemWidth})` : undefined;
  const {
    baseLegendStyles,
    baseLegendHorizontal,
    baseLegendVertical,
    baseLegendVerticalItem,
    baseLegendPaddingBottom,
    baseLegendPaddingTop,
    baseLegendPaddingEnd
  } = styles;

  return (
    <div
      className={classNames([
        baseLegendStyles,
        isHoriz ? baseLegendHorizontal : baseLegendVertical
      ])}
      style={{
        gridTemplateColumns: columTemplates,
        // set maxWidth to 100% on second render for text truncation to work.
        maxWidth: '100%'
      }}>
      {items.map((item: LegendItemData<K>, index: number) => {
        const {
          markerColor,
          markerShape,
          source,
          borderColor,
          lineColor,
          isCurrent,
          isFocused,
          isHidden,
          isHighlighted,
          key,
          actionable,
          text,
          lineStyle,
          lineWidth
        } = getItemProps<K>(
          item,
          index,
          sectionIdx,
          focusedItemInfo,
          isReadOnly,
          hoveredItemInfo,
          hiddenIds,
          highlightedIds
        );
        const isFocusedItem =
          focusedItemInfo.sectionIdx === sectionIdx && focusedItemInfo.itemIdx === index;
        const { gridRow, gridCol } = getGridCellIndices(
          items.length,
          index,
          props.itemsPerRow,
          props.itemsPerCol
        );
        const needsBottomSpace = gridRow != null && gridRow != props.itemsPerCol;
        const needsTopSpace = gridRow != null && gridRow != 1;
        return (
          <BaseLegendItem
            ref={isFocusedItem ? props.focusedItemRef : undefined}
            text={text}
            itemIdx={index}
            sectionIdx={sectionIdx}
            itemId={isTestEnv ? item.id : undefined}
            symbolHeight={symbolHeight}
            symbolWidth={symbolWidth}
            markerColor={markerColor}
            markerShape={markerShape}
            lineStyle={lineStyle}
            lineWidth={lineWidth}
            source={source}
            borderColor={borderColor}
            aria-label={item['aria-label']}
            lineColor={lineColor}
            isCurrent={isCurrent}
            isFocused={isFocused}
            isHidden={isHidden}
            isHighlighted={isHighlighted}
            key={key}
            gridRow={gridRow}
            gridCol={gridCol}
            minWidth={props.itemWidth}
            id={isCurrent ? activeId : ''}
            textColor={props.textColor}
            textFontFamily={props.textFontFamily}
            textFontSize={props.textFontSize}
            textFontStyle={props.textFontStyle}
            textFontWeight={props.textFontWeight}
            textTextDecoration={props.textTextDecoration}
            class={classNames([
              isHoriz ? '' : baseLegendVerticalItem,
              needsTopSpace ? baseLegendPaddingTop : '',
              needsBottomSpace ? baseLegendPaddingBottom : '',
              baseLegendPaddingEnd
            ])}
            hideAndShowBehavior={hideAndShowBehavior}
            hoverBehavior={hoverBehavior}
            actionable={actionable}
          />
        );
      })}
    </div>
  );
};
